import {PIXI} from '@colonist/pixi'
import {GameFont} from '../GameFont'
import {TextButtonView} from './TextButtonView'
import {SectionBgView} from './SectionBgView'
import {UIGamePlayerAvatarStatus} from '../ui_game/ui_game_players/UIGamePlayerAvatarView'
import {UIGameManager} from '../ui_game/UIGameManager'
import {UIGamePlayerAvatarContainerView} from '../ui_game/ui_game_popup/UIGamePlayerAvatarContainerView'
import {UIGameAnimation} from '../ui_game/UIGameEventController'
import {ProgressBarView} from './ProgressBarView'
import {Sound} from '../Sound'
import {ArrayUtils} from '../../../../shared/utils/ArrayUtils'
import {TranslationObject} from '@colonist/i18n'
import {GameColor} from '../GameColor'
import {VoteState} from '@colonist/game-state-types'
import {TextView} from './TextView'
import {PlayerColor} from '@colonist/game-states'

export class VoteView extends SectionBgView implements UIGameAnimation {
    private readonly uiGameManager: UIGameManager

    private playerAvatarsContainerFirstRow: UIGamePlayerAvatarContainerView
    private playerAvatarsContainerSecondRow: UIGamePlayerAvatarContainerView
    private firstLine: TextView
    private secondLine: TextView
    private buttonAccept: TextButtonView
    private buttonDecline: TextButtonView
    private readonly options: VoteState
    private timestamp: number
    private remainingTimeIndicator: ProgressBarView

    private readonly itemSize: number
    private readonly margin: number

    private readonly avatarSizeBig: number
    private readonly avatarSizeSmall: number

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, h: number, container: PIXI.Container, options: VoteState, accept: () => void, decline: () => void) {
        super(x, y, w, h, container, true)
        this.uiGameManager = uiGameManager
        this.options = options

        this.itemSize = w * 0.12
        this.margin = this.itemSize / 3

        this.avatarSizeBig = this.itemSize * 1.15
        this.avatarSizeSmall = this.itemSize

        const panelColor = GameColor.getColorHexValue(GameColor.Color.PrimaryBlue6)
        const outlineColor = GameColor.getColorHexValue(GameColor.Color.White)
        this.drawBg(panelColor, 1, 1, outlineColor, 0.2)
        this.drawTitle(options)
        this.drawSubtitle(options)

        this.timestamp = Date.now()
        this.drawRemainingTimeIndicator()

        this.initializePlayerAvatars()
        this.initializeVoteAcceptDeclineButtons(accept, decline)

        this.uiGameManager.eventController.registerUIGameAnimation(this)
        Sound.playSound(Sound.SoundType.VoteReminder)
    }

    private initializePlayerAvatars() {
        let y = this.remainingTimeIndicator.y + this.remainingTimeIndicator.height + this.margin

        if(!PIXI.utils.isMobile.any) {
            const numberOfPlayers = this.options.participatingPlayers.length
            const avatarSize = numberOfPlayers > 4 ? this.avatarSizeSmall : this.avatarSizeBig
            const maxSpacingToFitInOneLine = UIGamePlayerAvatarContainerView.calculateSpacingBetweenAvatars(this.width, avatarSize, numberOfPlayers)
            const spacing = Math.min(this.itemSize / 2, maxSpacingToFitInOneLine)
            this.playerAvatarsContainerFirstRow = this.initializeRowOfPlayerAvatars(y, avatarSize, this.options.participatingPlayers, spacing)
            this.loadDataIntoPlayerList(this.options)
            return
        }

        const first4Players = this.options.participatingPlayers.slice(0, 4)
        const second4Players = this.options.participatingPlayers.slice(4, 8)

        this.playerAvatarsContainerFirstRow = this.initializeRowOfPlayerAvatars(y, this.avatarSizeBig, first4Players, this.itemSize / 2)
        y += this.playerAvatarsContainerFirstRow.height + this.margin
        if(second4Players.length > 0) this.playerAvatarsContainerSecondRow = this.initializeRowOfPlayerAvatars(y, this.avatarSizeBig, second4Players, this.itemSize / 2)

        this.loadDataIntoPlayerList(this.options)
    }

    private initializeRowOfPlayerAvatars(y: number, avatarSize: number, players: PlayerColor[], spacing: number): UIGamePlayerAvatarContainerView {
        const newRow = new UIGamePlayerAvatarContainerView(this.uiGameManager, 0, y, this.width, avatarSize, players, this)
        newRow.applySpacingBetweenPlayerAvatarsAndCenterInContainer(spacing)
        newRow.removeRoundedBackground()
        newRow.resetAllPlayerAlpha()
        return newRow
    }

    loadDataIntoPlayerList(options: VoteState) {
        for(const player of options.participatingPlayers) {
            let status = UIGamePlayerAvatarStatus.Pending

            const acceptedVote = ArrayUtils.elementInArray(options.acceptedVotes, player)
            if(acceptedVote) status = UIGamePlayerAvatarStatus.Accepted

            const declinedVote = ArrayUtils.elementInArray(options.declinedVotes, player)
            if(declinedVote) status = UIGamePlayerAvatarStatus.Rejected

            const numberOfPlayersInFirstRow = this.playerAvatarsContainerFirstRow.playerAvatars.length
            if(ArrayUtils.elementInArray(options.participatingPlayers.slice(0, numberOfPlayersInFirstRow), player)) this.playerAvatarsContainerFirstRow.setPlayerStatus(player, status)
            else this.playerAvatarsContainerSecondRow.setPlayerStatus(player, status)
        }
    }

    private drawTitle(options: VoteState) {
        const style: Partial<PIXI.TextStyle> = {
            fontSize: GameFont.getResponsiveSize(GameFont.Size.XL),
            fill: GameColor.getColor(GameColor.Color.White),
            fontWeight: GameFont.Weight.Bold,
        }

        this.firstLine = new TextView(options.title, this.width / 2, this.margin, this.width, this.height, this)
        this.firstLine.changeStyle(style)
        this.firstLine.anchor.set(0.5, 0)
        this.firstLine.position.set(this.width / 2, this.margin)
    }

    private drawSubtitle(options: VoteState) {
        const style = {
            fontSize: GameFont.getResponsiveSize(GameFont.Size.Normal),
            fill: GameColor.getColor(GameColor.Color.White),
        }

        const voteText: TranslationObject = {
            key: 'strings:game.vote.playersRequiredForVoteToPass',
            options: {
                count: options.requiredNumberOfAcceptVotesToPass,
            },
        }
        this.secondLine = new TextView(voteText, this.width / 2, 0, this.width, this.height, this)
        this.secondLine.changeStyle(style)
        this.secondLine.anchor.set(0.5, 0)
        this.secondLine.position.set(this.width / 2, this.margin + this.secondLine.height * 1.5)
    }

    private initializeVoteAcceptDeclineButtons(accept: () => void, decline: () => void) {
        const spaceBetweenButtons = this.itemSize * 0.75
        const w = (this.width - spaceBetweenButtons - 2 * this.margin) / 2
        const y = this.height - this.margin - this.itemSize

        const buttonDeclineX = (this.width - spaceBetweenButtons) / 2 - w
        this.buttonDecline = new TextButtonView(buttonDeclineX, y, w, this.itemSize, this, GameFont.Size.Normal)
        this.buttonDecline.viewContainer.drawBg(0x000000, 0.2, 1, 0xFFFFFF)
        this.buttonDecline.updateAction(() => {
            decline()
            this.disableButtons()
            this.buttonAccept.viewContainer.drawBg(0x00000, 0.1, 1, 0xFFFFFF, 0.1)
            this.buttonAccept.getTextView().alpha = 0.5
        })
        this.buttonDecline.updateText({key: 'strings:colonistCommon.buttons.decline'})

        const buttonAcceptX = (this.width + spaceBetweenButtons) / 2
        this.buttonAccept = new TextButtonView(buttonAcceptX, y, w, this.itemSize, this, GameFont.Size.Normal)
        this.buttonAccept.viewContainer.drawBg(0x000000, 0.2, 1, 0xFFFFFF)
        this.buttonAccept.updateAction(() => {
            accept()
            this.disableButtons()
            this.buttonDecline.viewContainer.drawBg(0x00000, 0.1, 1, 0xFFFFFF, 0.1)
            this.buttonDecline.getTextView().alpha = 0.5
        })
        this.buttonAccept.updateText({key: 'strings:colonistCommon.buttons.accept'})
    }

    private disableButtons() {
        this.buttonAccept.cursor = 'auto'
        this.buttonAccept.interactive = false
        this.buttonDecline.cursor = 'auto'
        this.buttonDecline.interactive = false
        this.buttonAccept.returnToOriginalPosition()
        this.buttonDecline.returnToOriginalPosition()
    }

    private drawRemainingTimeIndicator() {
        const w = this.width - 2 * this.margin
        const h = this.itemSize / 7

        this.remainingTimeIndicator = new ProgressBarView(w, h, this, GameColor.getColorHexValue(GameColor.Color.PrimaryOrange0), undefined, 4)
        this.remainingTimeIndicator.x = (this.width - w) / 2
        this.remainingTimeIndicator.y = this.secondLine.y + this.secondLine.height + this.margin
        this.remainingTimeIndicator.percentage = 1 - (this.options.timePassed / this.options.duration)
    }

    onGameTick() {
        const now = Date.now()
        const timePassed = now - this.timestamp

        this.timestamp = now
        this.options.timePassed += timePassed
        this.remainingTimeIndicator.percentage = 1 - (this.options.timePassed / this.options.duration)

        this.playCloseToFinishWarningSound(timePassed)
    }

    private playCloseToFinishWarningSound(timePassed: number) {
        const percentageWhenCloseToFinish = 0.9
        const previousPercentage = (this.options.timePassed - timePassed) / this.options.duration
        const currentPercentage = this.options.timePassed / this.options.duration
        if(previousPercentage < percentageWhenCloseToFinish && currentPercentage >= percentageWhenCloseToFinish) {
            // TODO: Play custom sound when vote is close to finish
        }
    }

    destroy(options?: {children?: boolean, texture?: boolean, baseTexture?: boolean}): void {
        this.uiGameManager.eventController.unregisterUIGameAnimation(this)
        super.destroy(options)
    }
}
